import { Component, effect, inject, output } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { CheckboxModule } from 'primeng/checkbox';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { UniqueIdService } from '../../../services/builder/unique-id.service';
import { WidgetService } from '../../../services/builder/widget.service';
import { baseCustomPanelStructureComponent } from '../../new-custom-panel/baseCustomPanelStructure';
import { TextBoxCustomization } from './textbox-edit';

@Component({
  selector: 'lib-textbox-custom-panel',
  template: `
    <lib-base-cpanel
      [content]="content"
      [hideAutocompletion]="false"
      [form]="customizationForm"
      header="Personalizzazione Casella di Testo"
      (closed)="updateWidget($event)"
    />
    <ng-template #content>
      @if (customizationForm) {
        <div
          [formGroup]="customizationForm"
          class="flex flex-col gap-2 overflow-y-auto"
        >
          <div class="flex w-full flex-col">
            <label for="desc">Descrizione</label>
            <textarea
              id="desc"
              pTextarea
              formControlName="description"
            ></textarea>
          </div>
          <div class="flex w-full flex-col">
            <label for="weight">Peso</label>
            <small id="weightHelp">
              Il peso determina quanto questo campo incide sul punteggio totale
              della sottosezione.<br />
              Un valore più alto indica maggiore importanza rispetto agli altri
              campi.<br />
              Imposta a 0 se non vuoi che questo campo contribuisca al
              punteggio.
            </small>
            <p-input-number
              [autofocus]="false"
              formControlName="weight"
              fluid
              formControlName="weight"
              ariaDescribedBy="weightHelp"
              mode="decimal"
              minFractionDigits="0"
              maxFractionDigits="2"
            ></p-input-number>
          </div>
          <div class="flex w-full flex-col">
            <label for="placeH">Segnaposto</label>
            <input
              id="placeH"
              type="text"
              pInputText
              formControlName="placeHolder"
            />
          </div>
          <div class="flex w-full flex-col">
            <label for="defVal">Valore predefinito</label>
            <textarea
              id="defVal"
              pTextarea
              formControlName="defaultValue"
              class="p-fluid w-full"
            ></textarea>
          </div>
          <div class="flex w-full flex-row">
            <label for="required" class="w-full self-start">
              Campo obbligatorio
            </label>
            <p-checkbox
              id="required"
              formControlName="required"
              [binary]="true"
              class="self-end"
            ></p-checkbox>
          </div>
        </div>
      }
    </ng-template>
  `,
  imports: [
    ReactiveFormsModule,
    baseCustomPanelStructureComponent,
    InputTextModule,
    TextareaModule,
    InputNumberModule,
    CheckboxModule,
    SelectModule,
    TextareaModule,
  ],
})
export class TextBoxCPanelComponent {
  private fb = inject(FormBuilder);
  protected widgetService = inject(WidgetService<TextBoxCustomization>);
  protected uniqueIdService = inject(UniqueIdService);

  protected customizationForm = this.fb.group({
    id: [
      '',
      [
        Validators.required,
        this.uniqueIdService.forbiddenIdValidator(
          this.widgetService.uniqueId ?? '',
        ),
        this.uniqueIdService.isEmptySpace(),
      ],
    ],
    description: [''],
    placeHolder: [''],
    defaultValue: [''],
    required: [false],
    weight: [0],
    showIfRule: ['', [this.uniqueIdService.idNotExistsValidator()]],
    autocompleteRule: ['', [this.uniqueIdService.idNotExistsValidator()]],
    // topology: [0],   //Da aggiungere
  });

  protected closed = output();

  constructor() {
    effect(() => {
      const widget = this.widgetService.widget();
      if (!widget) return;

      this.customizationForm.patchValue({
        id: this.widgetService.uniqueId,
        description: widget.customization.description,
        defaultValue: widget.customization.defaultValue,
        placeHolder: widget.customization.placeHolder,
        required: widget.customization.required,
        weight: widget.customization.weight,
        showIfRule: widget.customization.showIfRule,
        autocompleteRule: widget.customization.autocompleteRule,
        // topology: widget.customization['topology'] || 0,
      });
    });
  }

  updateWidget(success: boolean) {
    if (!success) {
      this.widgetService.settingVisibility.set(false);
      return;
    }

    const formValue = this.customizationForm.value;
    const id = formValue.id?.trim();
    if (!id) return;

    this.widgetService.updateWidget(id, {
      description: formValue.description,
      defaultValue: formValue.defaultValue,
      placeHolder: formValue.placeHolder,
      required: formValue.required,
      showIfRule: formValue.showIfRule || '',
      weight: formValue.weight,
      autocompleteRule: formValue.autocompleteRule || '',
    });
    this.widgetService.settingVisibility.set(false);
  }
}
